// Checkout supports a local sandbox order or a server-created APNB handoff.
//
// APNB collects card details on its hosted page. Its browser return is unsigned
// navigation, not proof of payment; the backend verifies provider status before
// granting credits, subject to the real-money activation gate.
//
// Orders are created before navigation in the Add credits panel. A reload
// reads the existing order rather than submitting another purchase.
//
// The local sandbox takes no payment and mints no credits. Its notice remains
// non-dismissible; live checkout follows the provider's TEST/LIVE mode.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom'

import { api } from '../../api/client'
import { isApiRecord, type ApiValue } from '../../api/responseDecoders'
import { useAuth } from '../../contexts/AuthContext'
import { useBooleanFlagValue, useStringFlagValue } from '../../hooks/useBooleanFlagValue'
import { useClientFlags } from '../../hooks/useClientFlags'
import { createNadDebug } from '../../utils/debugLog'
import { canSeeBilling } from './billingAccess'
import {
  decodeCheckoutDestination,
  formatCredits,
  formatMoney,
  type CheckoutHandoff,
  type CheckoutOrder,
  type SandboxOrder,
} from './billingTypes'
import {
  trackCheckoutRedirectBlock,
  trackCheckoutRedirectSubmit,
} from '../../analytics/events'
import { rememberPendingCheckout } from './checkoutReturnStore'
import { getOrder, markPaid } from './sandboxOrderStore'
import SandboxNotice from './SandboxNotice'

const log = createNadDebug('dw:billing-sandbox')

/**
 * The live handoff, if this navigation carried one.
 *
 * Router state is the only channel that carries it: the code is a single-use
 * bearer credential, so it must not reach the URL, localStorage or a log line.
 * The cost of that choice is that it does not survive a reload — handled below
 * by sending the user back to start again rather than pretending to have it.
 *
 * Re-validated rather than trusted. It arrives as `unknown` from the router,
 * and the two fields that matter are the credential and the destination it gets
 * POSTed to; an http:// or relative destination would be a credential leak.
 */
function readHandoff(state: ApiValue): CheckoutHandoff | null {
  if (!isApiRecord(state) || !isApiRecord(state.handoff)) return null
  const h = state.handoff
  const { orderId, handoffCode, bootstrapUrl, expiresAt, credits, paymentContainerMode } = h
  if (typeof orderId !== 'string' || orderId === '') return null
  if (typeof handoffCode !== 'string' || handoffCode === '') return null
  if (typeof bootstrapUrl !== 'string' || !bootstrapUrl.startsWith('https://')) return null
  if (typeof expiresAt !== 'number' || !Number.isFinite(expiresAt)) return null
  if (typeof credits !== 'number' || !Number.isInteger(credits)) return null
  if (paymentContainerMode !== 'TEST' && paymentContainerMode !== 'LIVE') return null
  if (!isApiRecord(h.price)) return null
  const { amountMinor, currency } = h.price
  if (typeof amountMinor !== 'number' || !Number.isInteger(amountMinor)) return null
  if (typeof currency !== 'string' || currency === '') return null
  let destination
  try {
    if (!isApiRecord(h.destination)) return null
    destination = decodeCheckoutDestination({ kind: h.destination.kind, team_id: h.destination.teamId })
  } catch { return null }
  return {
    orderId,
    handoffCode,
    bootstrapUrl,
    expiresAt,
    credits,
    paymentContainerMode,
    destination,
    price: { amountMinor, currency },
  }
}

export default function CheckoutPage() {
  const { t, i18n } = useTranslation('settings')
  const locale = i18n.language
  const navigate = useNavigate()
  const { user, isLoading: authLoading } = useAuth()
  const { isLoading: flagsLoading } = useClientFlags()
  const enabled = useBooleanFlagValue('billing_sandbox_enabled', false)
  const audience = useStringFlagValue('billing_audience', 'meta_only')
  const [params] = useSearchParams()
  const [placing, setPlacing] = useState(false)
  const [placeError, setPlaceError] = useState<string | null>(null)

  const orderId = params.get('order')
  const userId = user?.id ?? 'anonymous'
  const order: SandboxOrder | null = useMemo(
    () => (orderId === null ? null : getOrder(userId, orderId)),
    [orderId, userId],
  )

  // A live handoff, if Pay now created a real order and passed it through.
  // Read once: router state survives re-renders but not a reload, and
  // re-reading during the submit would race the navigation away.
  const location = useLocation()
  const handoffRef = useRef<CheckoutHandoff | null>(readHandoff(location.state))
  const handoff = handoffRef.current
  const formRef = useRef<HTMLFormElement | null>(null)
  const [submitted, setSubmitted] = useState(false)
  const submittedOrderRef = useRef<string | null>(null)

  const allowed = canSeeBilling(user, undefined, audience)
  // Resolve the guard only once auth AND flags have settled. `useBooleanFlagValue`
  // collapses "not loaded" into the default (false), so redirecting eagerly
  // would bounce a legitimate visitor off their own checkout on first paint.
  const guardReady = !authLoading && !flagsLoading

  // A live order re-entered without router state — a reload, or the browser
  // coming back to this URL. The handoff cannot be recovered (single-use,
  // server-minted, deliberately never stored), but the ORDER can, and showing
  // what it was beats the sandbox "unknown order" dead end.
  const [liveOrder, setLiveOrder] = useState<CheckoutOrder | null>(null)
  const [liveOrderChecked, setLiveOrderChecked] = useState(false)

  useEffect(() => {
    if (handoff !== null || orderId === null || !guardReady) return
    let cancelled = false
    void (async () => {
      try {
        const found = await api.getCheckoutOrder(orderId)
        if (!cancelled) setLiveOrder(found)
      } catch {
        // A 404 here is the ordinary case for a placeholder order id, which is
        // not ours to resolve — the sandbox branch below handles it.
        if (!cancelled) setLiveOrder(null)
      } finally {
        if (!cancelled) setLiveOrderChecked(true)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [guardReady, handoff, orderId])

  useEffect(() => {
    if (!guardReady) return
    if (!allowed || !enabled) {
      log({ event: 'checkout:blocked', trace_id: 'billing-sandbox:guard', allowed, enabled })
      navigate('/dashboard', { replace: true })
    }
  }, [guardReady, allowed, enabled, navigate])

  // Live: hand the browser to Meta's checkout host.
  //
  // A form POST, not a redirect. `handoffCode` is a single-use bearer
  // credential, and a GET would put it in the URL — history, server logs, and
  // the Referer header on the next hop. The form is auto-submitted on mount so
  // this page is a transition rather than a screen; the button below exists
  // only for the case where scripting is blocked.
  //
  // This POST is cross-origin, so it needs `payments.apnb-api.com` in the CSP
  // `form-action` allowlist (#3455, in this PR) — without it the browser
  // refuses silently and the user sits on a blank hop with nothing in the
  // console but a CSP report.
  // checkout.redirect.block (#3503) — an expired handoff is the one block
  // reason we can observe from here. `csp_form_action` and `network` are not
  // detectable client-side: a form-action violation aborts the navigation with
  // nothing but a CSP report, which is exactly why the CSP entry (#3455) is
  // the load-bearing part and this event cannot substitute for it.
  const expiredLoggedRef = useRef(false)
  useEffect(() => {
    if (handoff === null || expiredLoggedRef.current) return
    if (Date.now() / 1000 < handoff.expiresAt) return
    expiredLoggedRef.current = true
    trackCheckoutRedirectBlock({ mft_order_id: handoff.orderId, reason: 'handoff_expired' })
  }, [handoff])

  const submitHandoff = useCallback(() => {
    if (handoff === null || submittedOrderRef.current === handoff.orderId) return
    // The guard below renders a loading div until auth and flags settle, so on
    // first paint there is no form yet. Marking `submitted` here anyway — as an
    // earlier version did — burned the one chance to fire: the deps do not
    // change when the form finally mounts, so the effect never ran again and
    // the log recorded a submission that never happened. Wait for the form, and
    // claim the single attempt only once the form is mounted and guards allow it.
    if (!guardReady || !allowed || !enabled) return
    const form = formRef.current
    if (form === null) return
    if (Date.now() / 1000 >= handoff.expiresAt) return
    // Claim synchronously before telemetry or submit: React state is not flushed
    // before StrictMode replays this effect. A handoff can be redeemed only once.
    submittedOrderRef.current = handoff.orderId
    setSubmitted(true)
    log({
      event: 'checkout:bootstrap_submit',
      trace_id: `billing:${handoff.orderId}`,
      mode: handoff.paymentContainerMode,
    })
    // Both of these happen BEFORE submit, because after it this document is
    // gone: the note telling the return page which order we left for, and the
    // last funnel event we can emit on our own origin. Everything between here
    // and checkout.return.* is on Meta's side, where we cannot see.
    rememberPendingCheckout(userId, handoff.orderId)
    trackCheckoutRedirectSubmit({
      mft_order_id: handoff.orderId,
      payment_container_mode: handoff.paymentContainerMode,
      handoff_ttl_remaining_s: Math.max(0, Math.round(handoff.expiresAt - Date.now() / 1000)),
    })
    form.submit()
  }, [allowed, enabled, guardReady, handoff, userId])

  useEffect(() => {
    submitHandoff()
  }, [submitHandoff])

  const placeOrder = useCallback(() => {
    if (orderId === null || order === null || placing) return
    setPlacing(true)
    setPlaceError(null)
    // Idempotent: replaying a placed order returns the existing record rather
    // than moving paidAt, so a double-click or a refreshed return converges.
    const result = markPaid(userId, orderId)
    if (!result.ok) {
      // Navigating anyway would show "Payment successful" for an order that was
      // never recorded — a reload would then restore it as pending. Stay put
      // and say what happened.
      log({
        event: 'checkout:place_failed',
        trace_id: `billing-sandbox:${orderId}`,
        reason: result.reason,
      })
      setPlaceError(result.reason)
      setPlacing(false)
      return
    }
    log({
      event: 'checkout:placed',
      trace_id: `billing-sandbox:${orderId}`,
      credits: result.order.credits,
    })
    navigate(
      `/dashboard?settings=usage-credits&payment=success&order=${encodeURIComponent(orderId)}`,
      { replace: true },
    )
  }, [navigate, order, orderId, placing, userId])

  if (!guardReady) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#0b0b0d] text-sm text-white/50">
        {t('settingsModal.billing.loadingCheckout')}
      </div>
    )
  }
  if (!allowed || !enabled) return null

  // ---- live: a real order, handed to Meta's checkout host ------------------
  if (handoff !== null) {
    const expired = Date.now() / 1000 >= handoff.expiresAt
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#0b0b0d] px-4 py-16">
        <div
          className="w-full max-w-[520px] rounded-3xl border border-white/[0.07] bg-[rgba(18,18,21,0.9)] p-6"
          data-testid="checkout-bootstrap"
        >
          {handoff.paymentContainerMode === 'TEST' && <SandboxNotice className="mb-5" />}

          {expired ? (
            // The handoff outlived its TTL before we could use it. Nothing here
            // can renew it — the code is single-use and minted server-side — so
            // send the user back to start a fresh attempt, which reuses the same
            // DCP order rather than creating a second one.
            <div className="flex flex-col gap-4" data-testid="checkout-handoff-expired">
              <p className="text-sm text-destructive">
                {t('settingsModal.billing.handoffExpired')}
              </p>
              <button
                type="button"
                onClick={() => navigate('/dashboard?settings=usage-credits')}
                className="h-10 rounded-full bg-white/[0.12] text-sm font-medium text-white hover:bg-white/20"
              >
                {t('settingsModal.billing.backToCredits')}
              </button>
            </div>
          ) : (
            <>
              <p className="text-sm text-white/70" data-testid="checkout-redirecting">
                {t('settingsModal.billing.redirectingToPayment', {
                  amount: formatMoney(handoff.price, locale),
                })}
              </p>
              {/*
                The credential travels in a hidden field of a POST body, never in
                the URL. `target` is deliberately absent: this is a top-level
                navigation onto Meta's origin, which is what the card page
                requires (it refuses to be framed).
              */}
              <form
                ref={formRef}
                method="POST"
                action={handoff.bootstrapUrl}
                onSubmit={(event) => {
                  event.preventDefault()
                  submitHandoff()
                }}
                data-testid="checkout-bootstrap-form"
              >
                <input type="hidden" name="handoff_code" value={handoff.handoffCode} />
                <button
                  type="submit"
                  disabled={submitted}
                  className="mt-4 h-11 w-full rounded-full bg-white text-sm font-semibold text-black hover:bg-white/90"
                >
                  {t('settingsModal.billing.continueToPayment')}
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    )
  }

  // A live order we recovered, but with no handoff to submit. The code is
  // single-use and gone; starting again reuses the same DCP order rather than
  // creating a second one, which is why this points back rather than retrying
  // in place.
  if (liveOrder !== null) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#0b0b0d] px-4 py-16">
        <div
          className="w-full max-w-[520px] rounded-3xl border border-white/[0.07] bg-[rgba(18,18,21,0.9)] p-6"
          data-testid="checkout-session-ended"
        >
          <h1 className="mb-4 text-xl font-semibold text-white">
            {t('settingsModal.billing.checkoutTitle')}
          </h1>
          <p className="mb-2 text-sm text-white/70">
            {t('settingsModal.billing.packLabel', {
              credits: formatCredits(liveOrder.credits, locale),
            })}
            {liveOrder.price !== null && ` · ${formatMoney(liveOrder.price, locale)}`}
          </p>
          <p className="mb-5 text-sm text-destructive">
            {t('settingsModal.billing.sessionEnded')}
          </p>
          <button
            type="button"
            onClick={() => navigate('/dashboard?settings=usage-credits')}
            className="h-10 w-full rounded-full bg-white/[0.12] text-sm font-medium text-white hover:bg-white/20"
          >
            {t('settingsModal.billing.backToCredits')}
          </button>
        </div>
      </div>
    )
  }

  // Still asking the server whether this id is one of ours. Rendering the
  // sandbox unknown-order state first would flash an error for a valid live
  // order.
  if (handoff === null && orderId !== null && !liveOrderChecked) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#0b0b0d] text-sm text-white/50">
        {t('settingsModal.billing.loadingCheckout')}
      </div>
    )
  }

  return (
    <div className="flex min-h-screen items-start justify-center bg-[#0b0b0d] px-4 py-16">
      <div
        className="w-full max-w-[520px] rounded-3xl border border-white/[0.07] bg-[rgba(18,18,21,0.9)] p-6"
        data-testid="checkout-page"
      >
        <h1 className="mb-4 text-xl font-semibold text-white">
          {t('settingsModal.billing.checkoutTitle')}
        </h1>

        <SandboxNotice className="mb-5" />

        {order === null ? (
          // Unknown, foreign or expired order — an error state, not a crash.
          <div data-testid="checkout-unknown-order" className="flex flex-col gap-4">
            <p className="text-sm text-destructive">
              {t('settingsModal.billing.checkoutUnknownOrder')}
            </p>
            <button
              type="button"
              onClick={() => navigate('/dashboard?settings=usage-credits')}
              className="h-10 rounded-full bg-white/[0.12] text-sm font-medium text-white hover:bg-white/20"
            >
              {t('settingsModal.billing.backToCredits')}
            </button>
          </div>
        ) : (
          <div className="flex flex-col gap-5">
            <div className="overflow-hidden rounded-2xl bg-white/[0.06]">
              <div className="flex items-center justify-between px-4 py-3">
                <span className="text-sm text-white/50">
                  {t('settingsModal.billing.product')}
                </span>
                <span className="text-sm text-white">
                  {t('settingsModal.billing.packLabel', {
                    credits: formatCredits(order.credits, locale),
                  })}
                </span>
              </div>
              <div className="flex items-center justify-between border-t border-white/[0.06] px-4 py-3">
                <span className="text-sm text-white/50">
                  {t('settingsModal.billing.total')}
                </span>
                <span className="text-sm font-medium text-white">
                  {formatMoney(order.price, locale)}
                </span>
              </div>
            </div>

            <div
              className="rounded-2xl border border-white/[0.07] px-4 py-3"
              data-testid="checkout-payment-method"
            >
              <p className="text-sm text-white">
                {t('settingsModal.billing.testPaymentMethodSelected')}
              </p>
              <p className="mt-0.5 text-xs font-medium text-white/45">
                {t('settingsModal.billing.testPaymentMethodHint')}
              </p>
            </div>

            {placeError !== null && (
              <p className="text-sm text-destructive" data-testid="checkout-place-error">
                {t('settingsModal.billing.placeOrderFailed')}
              </p>
            )}

            <button
              type="button"
              data-testid="checkout-place-order"
              disabled={placing || order.status === 'paid'}
              onClick={placeOrder}
              className="h-11 rounded-full bg-white text-sm font-semibold text-black transition-colors hover:bg-white/90 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {order.status === 'paid'
                ? t('settingsModal.billing.alreadyPlaced')
                : t('settingsModal.billing.placeOrder')}
            </button>
          </div>
        )}
      </div>
    </div>
  )
}
